import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useFragment } from "react-relay";
import { Pagination } from "swiper/modules";
import type { SwiperProps } from "swiper/react";

import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { MedalWidget } from "~/components/olympic-widgets/medal-widget";
import type { Props as ContentSchemaRenderProps } from "~/components/schema-render/content";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import type { hooksOlympicWidgetsQuery$key } from "~/queries/__generated__/hooksOlympicWidgetsQuery.graphql";
import type { olympicWidgetsQuery$key } from "~/queries/__generated__/olympicWidgetsQuery.graphql";

import { OlympicWidgetsHeader } from "./header";
import { useOlympicScmpWidgetConfig } from "./hooks";
import {
  Container,
  MedalWidgetWrapper,
  OlympicTodayHighlightsWrapper,
  OlympicTodayStoriesWrapper,
  SlideHeader,
  StyledSwiper,
  StyledSwiperSlide,
  Wrapper,
} from "./styles";
import { OlympicTodayHighlights } from "./today-highlights";
import { OlympicTopStories } from "./top-stories";

type Props = {
  className?: string;
  isArticle?: boolean;
  isMainSection?: boolean;
  pageType: "section" | "article";
  reference?: hooksOlympicWidgetsQuery$key;
  topStoriesReference?: olympicWidgetsQuery$key;
} & Pick<ContentSchemaRenderProps, "advertising">;

export const OlympicWidgets: FunctionComponent<Props> = ({
  advertising,
  className,
  isArticle = false,
  isMainSection,
  pageType,
  reference,
  topStoriesReference,
}) => {
  const swiperSettings = {
    breakpoints: isArticle
      ? {
          768: {
            direction: "vertical",
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }
      : {
          0: {
            direction: "vertical",
            slidesPerView: 3,
            spaceBetween: 24,
          },
        },
    direction: "horizontal",
    modules: [Pagination],
    pagination: {
      clickable: true,
    },
    slidesPerView: 1,
    spaceBetween: 0,
  } as SwiperProps;

  const appConfig = useOlympicScmpWidgetConfig(reference);

  const displayMedalWidget = appConfig?.olympics_dpa?.medal?.enable === "1";
  const displayTodayHighlights = appConfig?.olympics_today_highlight === "1";
  const olympicsDpaSource = appConfig?.olympics_dpa?.src ?? "";
  const olympicsDpaPath = appConfig?.olympics_dpa?.path ?? "";

  const topStories = useFragment(
    graphql`
      fragment olympicWidgetsQuery on Query {
        queue(filter: { name: "section_top_518881" }) {
          items(first: 2) {
            edges {
              ...topStoriesOlympicQueueItemsEdge
              node {
                ... on Content {
                  entityId
                }
              }
            }
          }
        }
      }
    `,
    topStoriesReference,
  );

  const listOfArticles = (
    topStories?.queue?.items?.edges?.map(item => item.node.entityId) ?? []
  ).join(",");

  const ga4ImpressionEvent = (): RecirculationWidgetImpressionEvent => ({
    action: "imp",
    category: "recirculation",
    customized_parameters: {
      list_of_articles: listOfArticles,
      page_type: pageType,
      widget_name: "olympics2024",
    },
    subcategory: "widget",
  });

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        intersectionThreshold: [0.1],
        isSendGA4Tracking: true,
        shouldSendOnce: true,
        shouldThrottle: true,
      },
    });

  if (appConfig?.olympics_widgets === "0") return null;
  return (
    <ErrorBoundary fallback={<></>}>
      <Container
        $isArticle={isArticle}
        className={className}
        ref={captureTrackImpressionEventTargetElement}
      >
        <OlympicWidgetsHeader advertising={advertising} pageType={pageType} />
        <Wrapper>
          <StyledSwiper {...swiperSettings}>
            {displayTodayHighlights && (
              <StyledSwiperSlide $isArticle={isArticle} $order={2}>
                <OlympicTodayHighlightsWrapper>
                  <ClientSideSuspense fallback={<></>}>
                    <OlympicTodayHighlights reference={reference} />
                  </ClientSideSuspense>
                </OlympicTodayHighlightsWrapper>
              </StyledSwiperSlide>
            )}
            {!isMainSection && topStoriesReference && (
              <StyledSwiperSlide $isArticle={isArticle} $order={3}>
                <OlympicTodayStoriesWrapper>
                  <OlympicTopStories
                    pageType={pageType}
                    reference={topStories?.queue?.items?.edges}
                  />
                </OlympicTodayStoriesWrapper>
              </StyledSwiperSlide>
            )}
            {displayMedalWidget && (
              <StyledSwiperSlide $isArticle={isArticle}>
                <MedalWidgetWrapper>
                  <SlideHeader>MEDALS TABLE</SlideHeader>
                  <MedalWidget path={olympicsDpaPath} src={olympicsDpaSource} />
                </MedalWidgetWrapper>
              </StyledSwiperSlide>
            )}
          </StyledSwiper>
        </Wrapper>
      </Container>
    </ErrorBoundary>
  );
};

OlympicWidgets.displayName = "OlympicWidgets";
